import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, tableDividerColumnWithAgg, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AgtFitnessVm } from 'app/stores/ui/agt.fitness.ui.store';
import { formatPercentWithIcon } from 'app/utils';
import { useLocation } from 'react-router';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import routes from 'routes';
import { FitnessTrackerGraphics } from './FitnessTrackerGraphics';
import { amisNowProps } from 'app/stores/ui/agt.amisnow.ui.store';
import { EChartsOption, ECharts, init } from 'echarts';
import { debounce } from 'lodash';
import _ from 'lodash';
import colors from 'assets/scss/colors.module.scss';
import { AmisNowHistoryChart } from './FitnessTrackerAmisNowHistory';

export const FitnessTrackerAmisNow = observer(() => {
	const { berichteUiiStore, agtFitnessUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [filteredAgtIds, setFilteredAgtIds] = useState<number[]>([]);

	const [selectedAgtId, setSelectedAgtId] = useState<number | undefined>(undefined);
	const history = useHistoryWithQuery();
	const location = useLocation();

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		/// AMIS.NOW
		const columns = amisNowProps.sort((a, b) => a.sort - b.sort);
		columns.forEach((prop, i) => {
			const isCategoryChange = i === 0 || (i > 0 && prop.adzCategory !== columns[i - 1].adzCategory);
			cols.push({
				label: prop.label,
				path: prop.path,
				sortBy: prop.path,
				cellModifier: `${isCategoryChange && 'border-left-turquoise'} bg-turquoise`,
				labelModifier: `${isCategoryChange && 'border-left-turquoise'} bg-turquoise`,
				format: prop.path === 'amisNow.aggregated.quoteRead' ? (val) => formatPercentWithIcon(val * 100, 1) : undefined,
				agg: {
					aggType: prop.path === 'amisNow.aggregated.quoteRead' ? AggregrationType.avg : AggregrationType.sum,
					format: prop.path === 'amisNow.aggregated.quoteRead' ? (val) => formatPercentWithIcon(val * 100, 1) : undefined,
				},
			});
		});
		// cols.find((c) => c.path === 'amisNow.item.quoteRead')!.format = (val) => formatPercentWithIcon(val * 100);

		cols.push(tableDividerColumnWithAgg);
		// StS Plus
		cols.push({
			label: (
				<>
					<span className="no-wrap">StS Vertrieb</span> <span className="no-wrap">Schulungsfortschritt ✚</span>
				</>
			),
			path: `stsBasicSchulung`,
			sortBy: `stsBasicSchulung`,
			cellModifier: 'bg-turquoise',
			labelModifier: 'bg-turquoise',
			render(row) {
				if (!row.data.stsBasicSchulung) return <></>;
				return <>{row.data.stsBasicSchulung}%</>;
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.onRowClick = (row) => {
			const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'amisnowaufgaben', row.data.agt.agtId.toString());
			history.pushWithQuery(p);
		};

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			// Store all agency IDs for aggregated chart view
			if (items.length > 0) {
				const agtIds = items.map((item) => item.agt.agtId);
				setFilteredAgtIds(agtIds);
			} else {
				setFilteredAgtIds([]);
			}
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtFitnessUiStore.items, agtFitnessUiStore]);

	useEffect(() => {
		// das hier ginge auch nicer, via treeNode Model und openByPath
		const lastPathElement = location.pathname.split('/').pop();
		let agtId;
		if (typeof lastPathElement === 'string') {
			agtId = parseInt(lastPathElement);
		}
		if (agtId && !isNaN(agtId)) {
			setSelectedAgtId(agtId);
		} else {
			setSelectedAgtId(undefined);
		}
	}, [location]);

	const onClose = () => {
		const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'amisnowaufgaben');
		history.pushWithQuery(p);
	};

	return (
		<>
			<div className="table-container">
				{!hasItems ? (
					<div className="pad-1rem">Keine Fitnesstrackerdaten gefunden</div>
				) : (
					<>
						{!selectedAgtId && filteredAgtIds.length > 0 && (
							<div className="pad-1rem pad-bottom-2rem is-sticky">
								<AmisNowHistoryChart agtIds={filteredAgtIds} />
							</div>
						)}
						{selectedAgtId && <FitnessTrackerGraphics agtId={selectedAgtId} view="amis" onClose={onClose} />}
						<Table stickyHeader={true} stickyFooter={true} tm={tm}></Table>
					</>
				)}
			</div>
		</>
	);
});

interface IFitnessTrackerAmisChart {
	fitVm: AgtFitnessVm;
}
export const FitnessTrackerAmisChart = observer((props: IFitnessTrackerAmisChart) => {
	const { fitVm } = props;

	const excludes = ['amisNow.aggregated.summe', 'amisNow.aggregated.davonRead', 'amisNow.aggregated.quoteRead'];
	const columns = amisNowProps.sort((a, b) => b.sort - a.sort).filter((p) => excludes.indexOf(p.path) === -1);

	// echarts stuff
	const chartRef = useRef<HTMLDivElement>(null);
	const sourceData = columns.map((c) => ({ label: c.label, val: _.get(fitVm, c.path) }));

	useEffect(() => {
		const dim = ['label', 'val'];
		const chartHeight = 500;

		let option: EChartsOption = {
			color: colors.turquoise,
			dataset: {
				dimensions: dim,
				source: sourceData,
			},
			yAxis: {
				type: 'category',
				axisLabel: {
					interval: 0,
					fontSize: 14,
					padding: [0, 5, 0, 0],
				},
			},
			xAxis: {
				type: 'value',
			},
			series: {
				type: 'bar',
				encode: {
					y: 'label',
					x: 'val',
				},
			},
			legend: {
				show: true,
			},
			tooltip: {
				trigger: 'item',
			},
			grid: {
				containLabel: true,
			},
		};

		let chart: ECharts | null = null;
		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg', height: chartHeight });
			chart.setOption(option);
		}
		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize.cancel);
		};
	}, [chartRef, sourceData]);

	return (
		<>
			<div style={{ width: '100%', height: 'auto', background: '#ffffff' }} ref={chartRef}></div>
		</>
	);
});
